import { Schema } from "effect"
import { Effort } from "./common"
import { WakeBatch } from "./domain/agentlife"
import {
  AgentId,
  ArtifactId,
  Epoch,
  FrameSeq,
  Millis,
  NonNegInt,
  ProfileId,
  RunId,
  RunnerId,
  Sha256,
  UserId,
} from "./ids"
import { ContextPack } from "./job"
import { ActivityUsage, Handoff, RunError, SessionRef, StopReason, Usage } from "./run"
import { AccessMode, AccessRequest, McpApproval } from "./runner"

// #region stream
/**
 * AI SDK 7 UIMessageChunk (ai@7.0.134: 28 variants + data-*), passed through untouched, with a `type` allowlist:
 * the mind sends content chunks only. The body owns the message frame (start, finish, abort, error,
 * message-metadata) and synthesises tool-approval-request/-response from permission.request/-response, so a
 * mind can never fake an approval card that bypasses the ledger and the owner check.
 */
export const MindChunkType = Schema.String.check(
  Schema.isPattern(
    /^(text-(start|delta|end)|reasoning-(start|delta|end|file)|tool-input-(start|delta|available|error)|tool-output-(available|error|denied)|source-(url|document)|file|start-step|finish-step|reset-step|custom|data-[a-z0-9-]+)$/,
  ),
)
export const UIMessageChunk = Schema.StructWithRest(Schema.Struct({ type: MindChunkType }), [
  Schema.Record(Schema.String, Schema.Unknown),
])
/**
 * Mind-side `data-*` chunks the runner produces (fix wave §4.5); the sink stores them like any content chunk.
 * `data-cli-error`: the CLI reported an error mid-run. `data-cli-notice`: one line for the viewer.
 */
export const CliErrorData = Schema.Struct({ message: Schema.String, code: Schema.optionalKey(Schema.String) })
export const CliNoticeData = Schema.Struct({
  kind: Schema.Literals(["access", "mcp", "truncated"]),
  text: Schema.String,
})
/**
 * `data-cli-plan` (chat/transcript wave R5): the agent's live plan, one part per update, each replacing the last. Codex
 * `turn/plan/updated` normalized by the runner (`inProgress` → `in_progress`), so it uses Claude `TodoWrite`'s statuses.
 */
export const CliPlanStepStatus = Schema.Literals(["pending", "in_progress", "completed"])
export const CliPlanData = Schema.Struct({
  explanation: Schema.NullOr(Schema.String),
  steps: Schema.Array(Schema.Struct({ step: Schema.String, status: CliPlanStepStatus })),
})
export const UIMessage = Schema.StructWithRest(
  Schema.Struct({ id: Schema.String, role: Schema.Literals(["user", "assistant", "system"]) }),
  [Schema.Record(Schema.String, Schema.Unknown)],
)
/** = Claude Agent SDK SessionStoreEntry { type, uuid?, timestamp?, …opaque }. The body dedupes on uuid. */
export const SessionStoreEntry = Schema.StructWithRest(
  Schema.Struct({
    type: Schema.String,
    uuid: Schema.optionalKey(Schema.String),
    timestamp: Schema.optionalKey(Schema.String),
  }),
  [Schema.Record(Schema.String, Schema.Unknown)],
)
/** The store key. The body's mirror is keyed by sessionId (proven); projectKey is recorded, not used for lookup. */
export const ClaudeSessionKey = Schema.Struct({
  projectKey: Schema.String,
  sessionId: Schema.String,
  subpath: Schema.optionalKey(Schema.String),
})

/**
 * Envelope, both directions: the brief's { v, agentId, turnId, seq, ts, type, payload } with turnId → runId
 * (worker and validator runs use the same envelope with agentId = null), seq → frameSeq (per direction, from 1,
 * gapless; never a log seq) and the lease epoch added (every sink fences on it).
 */
const envelope = {
  v: Schema.Literal(1),
  runId: RunId,
  agentId: Schema.NullOr(AgentId),
  epoch: Epoch,
  /**
   * Content frames are numbered from 1, gapless per direction. Control frames (`ack`, `turn.resume`, `heartbeat`)
   * carry frameSeq 0: they never consume a number and are never stored, acked or replayed (P7, A14).
   */
  frameSeq: FrameSeq,
  ts: Millis,
} as const
const msg = <T extends string, P extends Schema.Struct.Fields>(type: T, payload: P) =>
  Schema.Struct({ ...envelope, type: Schema.Literal(type), payload: Schema.Struct(payload) })

// ---- body → mind ----
export const TurnStart = msg("turn.start", {
  messages: Schema.Array(UIMessage), // new messages since the session ref (all of them when fresh); a worker gets its brief
  wake: Schema.NullOr(WakeBatch), // turns: the coalesced wakes
  contextPack: ContextPack, // hash must equal the signed job's contextPack.hash
  handoff: Schema.NullOr(Handoff),
  sessionRef: Schema.NullOr(SessionRef), // only if resumable on this executor
  settings: Schema.Struct({
    model: Schema.NullOr(Schema.String),
    effort: Schema.NullOr(Effort),
    maxTurns: Schema.NullOr(Schema.Int), // null = no cap; the signed job.limits are authoritative (W22)
    permissionMode: Schema.Literals(["ask", "policy_auto", "plan"]),
  }),
})
export const TurnSteer = msg("turn.steer", {
  messageId: Schema.String,
  message: UIMessage,
  /** now: interrupt the current tool call and continue with this; next: after the current tool call; later: next turn. */
  priority: Schema.Literals(["now", "next", "later"]),
})
export const TurnInterrupt = msg("turn.interrupt", {
  reason: Schema.Literals(["user_stop", "lease_revoked", "shutdown", "superseded"]),
  by: Schema.NullOr(UserId),
})
export const PermissionResponse = msg("permission.response", {
  requestId: Schema.String,
  inputHash: Sha256, // must match the request: the decision binds to the exact input
  decision: Schema.Literals(["allow", "deny"]),
  remember: Schema.Literals(["once", "this_turn"]),
  by: UserId, // the run's principal
  message: Schema.NullOr(Schema.String),
})
export const Ack = msg("ack", { frameSeq: FrameSeq }) // the sink durably stored every mind frame ≤ this
export const SessionLoad = msg("session.load", {
  requestId: Schema.String,
  key: ClaudeSessionKey,
  entries: Schema.NullOr(Schema.Array(SessionStoreEntry)),
  subkeys: Schema.Array(Schema.String),
  done: Schema.Boolean,
})
export const TurnResume = msg("turn.resume", { fromFrameSeq: FrameSeq, lastBodyFrameSeqSeen: FrameSeq })
export const BodyToMind = Schema.Union([
  TurnStart,
  TurnSteer,
  TurnInterrupt,
  PermissionResponse,
  Ack,
  SessionLoad,
  TurnResume,
]).pipe(Schema.toTaggedUnion("type"))

// ---- mind → body ----
export const TurnAccepted = msg("turn.accepted", {
  runnerId: RunnerId,
  profileId: ProfileId,
  sessionRef: Schema.NullOr(SessionRef),
  resumed: Schema.Literals(["native", "handoff_delta", "handoff_full", "fresh"]),
  capabilities: Schema.Struct({
    steer: Schema.Literals(["native", "between_tool_calls", "next_turn_only"]),
    interrupt: Schema.Boolean,
    permissionPrompts: Schema.Boolean,
  }),
  /** Fix wave §4.5: the access mode the run started with, and the MCP servers it loaded (names only). */
  access: Schema.optionalKey(
    Schema.Struct({
      mode: AccessMode,
      /** "auto · permissionMode auto" | "auto · workspace-write, on-request" */
      text: Schema.String,
      /** Names only, incl. minga/repo; from the CLI's own status when available. */
      mcpServers: Schema.Array(Schema.String),
      /** Settings wave (0.3.0 always sends it): MCP tools approval under Auto. Absent = unknown (older runners). */
      mcpApproval: Schema.optionalKey(McpApproval),
      /** The job's narrowing request, when it had one. */
      requested: Schema.optionalKey(AccessRequest),
    }),
  ),
})
export const Chunk = msg("chunk", { chunk: UIMessageChunk })
export const PermissionRequest = msg("permission.request", {
  requestId: Schema.String,
  tool: Schema.String,
  title: Schema.String,
  input: Schema.Unknown, // redacted on the runner first
  inputHash: Sha256,
  risk: Schema.Literals(["low", "medium", "high"]),
  /**
   * Chat/transcript wave (C1): the `toolCallId` of the tool call this prompt is about (Claude `toolUseID`, Codex
   * `itemId`), so viewers attach the card to that tool row. Absent on runners before the change.
   */
  toolCallId: Schema.optionalKey(Schema.String),
})
export const SessionRead = msg("session.read", { requestId: Schema.String, key: ClaudeSessionKey }) // serves SessionStore.load()/listSubkeys()
export const SessionAppend = msg("session.append", {
  key: ClaudeSessionKey,
  entries: Schema.NonEmptyArray(SessionStoreEntry),
})
/** Mechanical facts only; the agent's outcome claim went through report_outcome on the MCP endpoint. */
export const TurnCompleted = msg("turn.completed", {
  stopReason: StopReason,
  usage: Schema.Array(Usage),
  sessionRef: Schema.NullOr(SessionRef),
  artifacts: Schema.Array(ArtifactId),
  lastFrameSeq: FrameSeq,
})
export const TurnFailed = msg("turn.failed", {
  stopReason: StopReason,
  error: RunError,
  retryable: Schema.Boolean,
  usage: Schema.Array(Usage),
  sessionRef: Schema.NullOr(SessionRef),
})
export const Heartbeat = msg("heartbeat", {
  lastFrameSeq: FrameSeq,
  bodyFrameSeqSeen: FrameSeq,
  phase: Schema.Literals(["preparing", "running", "awaiting_permission", "finishing"]),
  idleMs: NonNegInt, // time since the CLI last produced an event: the stall watchdog's input
  /** The effective model, once the driver knows it (fix wave §4.3). */
  model: Schema.optionalKey(Schema.String),
  /** Token counts of the run so far (Codex thread/tokenUsage/updated, Claude message.usage sums). */
  usageSoFar: Schema.optionalKey(ActivityUsage),
})
export const MindToBody = Schema.Union([
  TurnAccepted,
  Chunk,
  PermissionRequest,
  SessionRead,
  SessionAppend,
  TurnCompleted,
  TurnFailed,
  Heartbeat,
]).pipe(Schema.toTaggedUnion("type"))

/** First frame on every (re)connected run stream, runner → sink, before any envelope. Token on the upgrade binds runId+epoch. */
export const StreamHello = Schema.Struct({
  type: Schema.Literal("hello"),
  runId: RunId,
  epoch: Epoch,
  runnerId: RunnerId,
  lastFrameSeqSent: FrameSeq,
  lastBodyFrameSeqSeen: FrameSeq,
})
/** Sink → runner on a refused hello or after the hub reported the lease gone. The runner stops the process. */
export const StreamClosed = Schema.Struct({
  type: Schema.Literal("closed"),
  reason: Schema.Literals(["stale_epoch", "lease_closed", "run_ended", "bad_token"]),
  currentEpoch: Epoch,
})

/** What the runner sends on the run stream socket (A14). */
export const RunnerStreamFrame = Schema.Union([StreamHello, MindToBody])
/** What the sink (the Run DO) sends on the run stream socket. */
export const SinkStreamFrame = Schema.Union([BodyToMind, StreamClosed])
// #endregion stream
